<template>
  <div ref="rootEl" class="charts-embedder" :class="{ 'charts-embedder--dark': isDark }" :style="containerStyle">

    <!-- ═══════════════ STEP 0: SETUP WIZARD ═══════════════ -->
    <ChartSetupWizard v-if="wizardActive" :datasetOptions="datasetOptions" :viewOptions="viewOptions"
      :dateGroupingPresets="dateGroupingPresets" :t="t" @update:dataset="selectedDataset = $event"
      @continue="onWizardContinue" />

    <!-- ═══════════════ CHART MODE ═══════════════ -->
    <template v-if="!wizardActive">

      <!-- IFRAME (always full size) -->
      <div class="charts-embedder__iframe-wrapper">
        <ChartIframe :key="iframeRenderKey" :url="resolvedUrl" :loading="isLoading" :loadingText="'Cargando...'"
          :emptyText="isProcessFormField && !resolvedUrl ? 'Selecciona una pregunta para visualizar la gráfica.' : 'Selecciona un modelo y gráfica para comenzar.'"
          @loaded="isLoading = false" />
      </div>

      <!-- GEAR TOGGLE -->
      <button v-if="showControls" class="charts-embedder__gear" :class="{ 'charts-embedder__gear--open': panelOpen }"
        :title="panelOpen ? 'Cerrar' : 'Configuración'" @click.stop="panelOpen = !panelOpen">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"
          stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="3" />
          <path
            d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      </button>

      <!-- OVERLAY PANEL -->
      <transition name="charts-embedder-overlay">
        <div v-if="showControls && panelOpen" class="charts-embedder__backdrop" @click.self="panelOpen = false">
          <div class="charts-embedder__controls" @click.stop>
            <!-- Dataset + Field + Chart + Time + Live -->
            <ChartControlPanel :dataset="selectedDataset" :view="selectedView" :field="selectedField"
              :field2="selectedField2" :from="selectedFrom" :to="selectedTo" :live="liveEnabled" :freq="selectedFreq"
              :showTimeRange="!omitsTimeRange"
              :stackGroup="stackGroupEnabled"
              :topRankedEnabled="topRankedEnabled" :topRankedCount="topRankedCount"
              :topRankedAvailable="topRankedAvailable" :topRankedOffered="topRankedOffered"
              :topRankedActive="topRankedActive" :topRankedLoading="topRankedLoading"
              :topRankedError="topRankedError" :topRankedUnavailableReason="topRankedUnavailableReason"
              :topRankedMin="TOP_N_MIN" :topRankedMax="TOP_N_MAX"
              :currentStatusOffered="currentStatusOffered" :currentStatusEnabled="currentStatusEnabled"
              :currentStatusLocked="currentStatusLocked"
              :currentStatusLabel="currentStatusLabel" :currentStatusApplied="currentStatusApplied"
              :currentStatusRelevant="currentStatusRelevant" :currentStatusAffects="currentStatusAffects"
              :currentStatusLoading="currentStatusLoading" :currentStatusError="currentStatusError"
              :openOnlyOffered="openOnlyOffered" :openOnlyEnabled="openOnlyEnabled"
              :openOnlyClosedValues="openOnlyClosedValues"
              :datasetOptions="datasetOptions" :viewOptions="viewOptions" :fieldOptions="effectiveFieldOptions"
              :dateGroupingPresets="dateGroupingPresets" :isProcessFormField="isProcessFormField"
              :showField2="!isProcessFormField" :field2FieldOptions="field2FieldOptions"
              :metric="selectedMetric" :metricOptions="metricOptions" :t="t"
              @update:dataset="selectedDataset = $event" @update:view="selectedView = $event"
              @update:field="selectedField = $event; selectedField2 = ''" @update:field2="selectedField2 = $event"
              @update:from="onFromChange" @update:to="onToChange" @update:live="liveEnabled = $event"
              @update:freq="selectedFreq = $event" @update:metric="selectedMetric = $event"
              @update:stackGroup="stackGroupEnabled = $event"
              @update:topRankedEnabled="setTopRankedEnabled" @update:topRankedCount="updateTopRankedCount"
              @commit-top-ranked="commitTopRankedCount"
              @update:currentStatusEnabled="setCurrentStatusEnabled" @update:openOnlyEnabled="setOpenOnlyEnabled"
              @download-csv="onDownloadCsv" @download-pdf="onDownloadPdf" />

            <!-- Dataset filters -->
            <ChartFilterPanel v-if="showFilters && hasSelection" :filterKeys="filterKeys" :activeFilters="activeFilters"
              :filterOptionsMap="filterOptionsMap" :getOptionsForFilter="getOptionsForFilter"
              :lockedFilters="lockedFilters"
              :translateFilter="translateFilter" :isProcessFormField="hasProcessForm"
              :selectedDataset="selectedDataset" :activeProcessFormLabel="activeProcessFormLabel"
              :processFormPairs="processFormPairs" :isMultiQuestionnaire="isMultiQuestionnaire"
              :questionnaireOptions="questionnaireOptions" :selectedQuestions="selectedQuestions"
              @filter-change="onFilterChange"
              @set-process-form-answer="({ question, answer }) => setProcessFormAnswerForQuestion(question, answer)"
              @clear-process-form-pairs="clearProcessFormPairs"
              @toggle-selected-question="toggleSelectedQuestion"
              @clear-selected-questions="clearSelectedQuestions"
              @questionnaire-change="setActiveProcessFormLabel" />

            <!-- URL bar -->
            <div v-if="hasSelection && user.is_master_admin" class="charts-embedder__url-bar">
              <input :value="editableUrl" @input="editableUrl = $event.target.value" @keydown.enter="applyManualUrl"
                class="charts-embedder__url-input" spellcheck="false" />
              <button class="charts-embedder__url-btn" :title="'Copiar'" @click="copyUrl">
                <span>{{ copied ? '✓' : '⧉' }}</span>
              </button>
              <button v-if="editableUrl !== resolvedUrl"
                class="charts-embedder__url-btn charts-embedder__url-btn--apply" :title="'Aplicar'"
                @click="applyManualUrl">
                <span>↵</span>
              </button>
            </div>
          </div>
        </div>
      </transition>
    </template>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount, toRefs } from '@vue/composition-api'
import store from '@/store'
import axios from '@axios'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

import ChartControlPanel from './components/ChartControlPanel.vue'
import ChartFilterPanel from './components/ChartFilterPanel.vue'
import ChartIframe from './components/ChartIframe.vue'
import ChartSetupWizard from './components/ChartSetupWizard.vue'

import { getDatasetDefaultFrom, getDatasetModule, getTimeseriesFields, getTimeseriesDefaultField, resolveLegacyLabel } from './config/datasets'
import { useChartTranslations } from './composables/useChartTranslations'
import { useChartDatasets } from './composables/useChartDatasets'
import { useChartFields } from './composables/useChartFields'
import { useChartFilters } from './composables/useChartFilters'
import { useChartFilterOptions } from './composables/useChartFilterOptions'
import { useChartUrl } from './composables/useChartUrl'
import { useChartDateGrouping } from './composables/useChartDateGrouping'
import { useProcessFormChart } from './composables/useProcessFormChart'
import { freezeTimeRange, resolveTimeBound } from './utils/timeUtils'
import { useTopRanked } from './composables/useTopRanked'
import { useCurrentStatus } from './composables/useCurrentStatus'
import { MAX_TOP_N, MIN_TOP_N } from './utils/topRanked'

export default {
  name: 'ChartsEmbedder',

  components: {
    ChartControlPanel,
    ChartFilterPanel,
    ChartIframe,
    ChartSetupWizard,
  },

  props: {
    /** Height of the component in pixels. Receives the slot's measured height. */
    height: {
      type: Number,
      default: 0,
    },
    /** Base URL of the Streamlit server. */
    baseUrl: {
      type: String,
      required: true,
    },
    /** Current language code. */
    lang: {
      type: String,
      default: 'en',
    },
    /** Enable real-time refresh when relative time ranges are used. Default is true. */
    live: {
      type: Boolean,
      default: true,
    },
    /** Refresh interval in seconds (used when live is true). */
    refreshInterval: {
      type: Number,
      default: 15,
    },
    /** Auth token for Streamlit. Omit to let backend resolve it. */
    token: {
      type: String,
      default: '',
    },
    // ─── External control props (SDK-ready) ─────────────
    /** Pre-selected dataset key. If set, the dataset selector starts with this value. */
    dataset: {
      type: String,
      default: '',
    },
    /** Pre-selected view key. If set, the view selector starts with this value. */
    view: {
      type: String,
      default: '',
    },
    /** Pre-applied filters object, e.g. { tenant: 'Acme', status: 'open' }. */
    filters: {
      type: Object,
      default: null,
    },
    /** Show or hide the control panel (dataset + view selectors). */
    showControls: {
      type: Boolean,
      default: true,
    },
    /** Show or hide the filter panel after selection. */
    showFilters: {
      type: Boolean,
      default: true,
    },
    // ─── Optional advanced Streamlit params ──────────────
    /** Aggregation method: sum, avg, count, min, max, median, stddev, variance. (gauge, stat) */
    agg: { type: String, default: '' },
    /** Group-by column(s). Comma-separated or array stringified. (bar, heatmap, ring, scatter, timeseries) */
    by: { type: String, default: '' },
    /** Columns to include in the query. Comma-separated. */
    columns: { type: String, default: '' },
    /** Time-axis frequency: 1m, 5m, 1h, 1d. (bar, timeseries) */
    freq: { type: String, default: '' },
    /** Inner radius of ring chart. 0 = full pie. (ring) */
    innerRadius: { type: [String, Number], default: '' },
    /** Legend position: top, bottom, left, right, none, hide. */
    legendPosition: { type: String, default: '' },
    /** Chart orientation: horizontal, vertical. (bar) */
    orientation: { type: String, default: '' },
    /** Bar stacking: zero, group. (bar) */
    stack: { type: String, default: '' },
    /** Draw trendline over each series. (timeseries) */
    trendline: { type: [String, Boolean], default: '' },
    /** Target range JSON: { mode, values }. (gauge, stat) */
    targetRange: { type: String, default: '' },
    /** Unit of measurement: L0d, L2d. (gauge, stat) */
    unit: { type: String, default: '' },
    /** Color mapping for heatmaps: hot, inferno, viridis. */
    colorMapping: { type: String, default: '' },
    /** Max rows to return. (dataframe, geo_heatmap, json, scatter, spatial_heatmap) */
    limit: { type: [String, Number], default: '' },
    /** Render as non-interactive plaintext. (dataframe, json) */
    plaintext: { type: [String, Boolean], default: '' },
    /** Data field/value mapping JSON. */
    mapping: { type: String, default: '' },
    /** Custom SQL query — overrides all other params. */
    query: { type: String, default: '' },
    /** Custom filter conditions JSON. */
    where: { type: String, default: '' },
    /** Aspect ratio of spatial heatmap frame. */
    aspectRatio: { type: [String, Number], default: '' },
    /** Frame access token. (spatial_heatmap) */
    frameToken: { type: String, default: '' },
    /** Render background image in spatial heatmap. */
    renderBackground: { type: [String, Boolean], default: '' },
    /** Render engine override: altair, vega, plotly, streamlit, pyplot, plaintext. */
    engine: { type: String, default: '' },
    /** Color palette keys for customization. */
    palette: { type: String, default: '' },
    /** Encrypted/encoded JSON configuration or plain JSON string. */
    configuration: { type: String, default: '' },
    /**
     * Persisted chart state to restore on reopen / page reload
     * (field, field2, metric, freq, stackGroup, filters, process-form…).
     * Emitted back to the host as `update:config` whenever it changes.
     */
    savedConfig: {
      type: Object,
      default: null,
    },
  },

  setup(props, { emit }) {
    const user = store.getters["user/getUser"];
    const toast = useToast()
    const { baseUrl, refreshInterval, token } = toRefs(props)

    // Persisted configuration used to restore the chart. Read once on mount;
    // the refs below are initialized from it so the dataset/field/view watchers
    // (which reset state) never fire for restored values.
    const saved = props.savedConfig || {}

    // ─── Language (local, defaults to '' = English / no param) ──
    const selectedLang = ref('')

    // ─── Root element ref + ResizeObserver ─────────────────
    const rootEl = ref(null)
    let resizeObserver = null

    // Épica 4: Debounced resize observer — re-renders the iframe on container resize
    const iframeRenderKey = ref(0)
    let resizeDebounceTimer = null

    // Debounced emit of the chart configuration to the host slot
    let configEmitTimer = null

    // ─── Escape key to close panel ─────────────────────
    const onEscapeKey = (e) => {
      if (e.key === 'Escape' && panelOpen.value) {
        panelOpen.value = false
      }
    }

    onMounted(() => {
      document.addEventListener('keydown', onEscapeKey)
      if (rootEl.value) {
        resizeObserver = new ResizeObserver(() => {
          clearTimeout(resizeDebounceTimer)
          resizeDebounceTimer = setTimeout(() => {
            iframeRenderKey.value++
          }, 250)
        })
        resizeObserver.observe(rootEl.value)
      }
    })

    onBeforeUnmount(() => {
      document.removeEventListener('keydown', onEscapeKey)
      clearTimeout(resizeDebounceTimer)
      clearTimeout(configEmitTimer)
      if (resizeObserver && rootEl.value) {
        resizeObserver.unobserve(rootEl.value)
        resizeObserver = null
      }
    })

    // ─── Container style from height prop ──────────────────
    const containerStyle = computed(() => {
      const h = props.height
      if (!h || h <= 0) return { height: '100%' }
      return { height: h + 'px' }
    })

    // ─── Panel open/close state ───────────────────────────
    const panelOpen = ref(false)


    // ─── Dark mode from app theme ─────────────────────────
    const isDark = computed(() => store.state.appConfig.layout.skin === 'dark')
    const theme = computed(() => isDark.value ? 'dark_banamex' : 'light_banamex')

    // ─── Datasets & Views ─────────────────────────────────
    const {
      selectedDataset,
      selectedView,
      datasetOptions,
      datasetRestriction,
      viewOptions,
      hasSelection,
    } = useChartDatasets({
      initialDataset: props.dataset,
      initialView: props.view,
    })

    // Filter keys locked by a permission-restricted access (read-only in UI)
    const lockedFilters = computed(() => datasetRestriction.value?.lockedFilters || [])

    // A dataset whose time filter is unusable declares omitTimeRange: its URL
    // travels with no from/to (useChartUrl drops them) and the date controls
    // of the wizard and the panel are hidden.
    const omitsTimeRange = computed(() => !!getDatasetModule(selectedDataset.value)?.omitTimeRange)

    // ─── Wizard state ──────────────────────────────────────
    const wizardActive = ref(!props.dataset && !props.view)
    const selectedField = ref(resolveLegacyLabel(selectedDataset.value, saved.field) ?? '')
    const selectedField2 = ref(saved.field2 ?? '')
    // Metric selector (only datasets that declare `metrics` show the dropdown)
    const selectedMetric = ref(saved.metric ?? '')

    // ─── Field options (by parameter) ─────────────────────
    const { fieldOptions } = useChartFields(selectedDataset, selectedLang)

    // ─── Translations (needs selectedDataset for module labels) ─────
    const { t, translateFilter } = useChartTranslations(selectedLang, selectedDataset)

    // ─── Filters ──────────────────────────────────────────
    const { activeFilters, filterKeys, setFilter, activeProcessFormLabel, processFormFilters, hasProcessForm, isMultiQuestionnaire, questionnaireOptions, setActiveProcessFormLabel, processFormMode, processFormAnswer, setProcessFormAnswer, processFormPairs, setProcessFormAnswerForQuestion, clearProcessFormPairs, selectedQuestions, toggleSelectedQuestion, clearSelectedQuestions, isQuestionCountMode, analysisAnswers, setAnalysisAnswer, clearAnalysisAnswers } = useChartFilters(
      selectedDataset,
      props.filters,
      { selectedField },
    )

    // ─── Restore persisted process-form state ──────────────
    // Assigned directly (not via setters) so the selectedField watcher — which
    // would clear these — doesn't run: selectedField was initialized, not changed.
    if (saved.processFormLabel) activeProcessFormLabel.value = resolveLegacyLabel(selectedDataset.value, saved.processFormLabel)
    if (Array.isArray(saved.processFormPairs) && saved.processFormPairs.length) {
      processFormPairs.value = saved.processFormPairs.map(p => ({ ...p }))
    }
    if (Array.isArray(saved.selectedQuestions) && saved.selectedQuestions.length) {
      selectedQuestions.value = [...saved.selectedQuestions]
    }
    if (saved.analysisAnswers && typeof saved.analysisAnswers === 'object') {
      Object.entries(saved.analysisAnswers).forEach(([q, a]) => setAnalysisAnswer(q, a))
    }

    // ─── Filter Options (API-backed + embed) ───────────
    const { filterOptionsMap, getOptionsForFilter } = useChartFilterOptions(selectedDataset, filterKeys, { baseUrl, token, activeFilters, activeProcessFormLabel, setFilter })

    // ─── Seed locked filters from the forced where ────────
    // When access is permission-restricted, reflect the forced `where` values
    // in the (disabled) filter control so operators see what it's pinned to.
    // Runs after useChartFilters' own dataset-change reset (registered first).
    watch([selectedDataset, datasetRestriction], () => {
      const restriction = datasetRestriction.value
      if (!restriction?.forcedWhere || !restriction.lockedFilters?.length) return
      const module = getDatasetModule(selectedDataset.value)
      if (!module?.filters) return
      restriction.lockedFilters.forEach(filterKey => {
        const moduleFilter = module.filters.find(f => f.key === filterKey)
        const column = moduleFilter?.whereColumn || filterKey
        const cond = restriction.forcedWhere[column]
        const values = cond && Array.isArray(cond.values) ? cond.values : null
        if (values && values.length > 0) {
          setFilter(filterKey, values.join(','))
        }
      })
    }, { immediate: true })

    // ─── Is the selected field a processForm field? ────
    const isProcessFormField = computed(() => {
      if (!selectedField.value) return false
      const opt = fieldOptions.value.find(f => f.value === selectedField.value)
      return opt ? opt.processForm : false
    })

    // ─── Clear fields when dataset changes + apply defaults ──
    watch(selectedDataset, () => {
      const module = getDatasetModule(selectedDataset.value)
      if (module?.defaults) {
        selectedField.value = module.defaults.field || ''
        selectedField2.value = module.defaults.field2 || ''
      } else {
        selectedField.value = ''
        selectedField2.value = ''
      }
      // Reset metric to dataset default (first option) or empty
      selectedMetric.value = module?.metrics?.options?.[0]?.value || ''
    })

    // ─── Metric options exposed to the control panel ─────────
    const metricOptions = computed(() => {
      const module = getDatasetModule(selectedDataset.value)
      return module?.metrics?.options || []
    })

    // Initialize metric on first load (watch above only fires on change).
    // Skip when a persisted metric was restored from savedConfig.
    if (saved.metric === undefined || saved.metric === null) {
      const initialModule = getDatasetModule(selectedDataset.value)
      selectedMetric.value = initialModule?.metrics?.options?.[0]?.value || ''
    }

    // ─── Date Grouping (freq) ─────────────────────────
    const { selectedFreq, dateGroupingPresets } = useChartDateGrouping(selectedDataset, selectedField, selectedView)

    // Restore persisted frequency (the field watcher won't clobber it on mount
    // because selectedField was initialized, not reassigned).
    if (saved.freq) selectedFreq.value = saved.freq

    // ─── Timeseries view logic ────────────────────────────
    const isTimeseries = computed(() => selectedView.value === 'timeseries')

    // Filtered field options: for timeseries campo1 shows only date fields
    const effectiveFieldOptions = computed(() => {
      if (!isTimeseries.value) return fieldOptions.value
      const tsFields = getTimeseriesFields(selectedDataset.value)
      if (!tsFields || tsFields.dateFields.length === 0) return fieldOptions.value
      return tsFields.dateFields.map(f => ({
        value: f.key,
        label: f.label,
        rawKey: f.key,
      }))
    })

    // Campo2 options: for timeseries shows non-date fields; for other views shows all
    // non-processForm fields except the ones already selected as campo 1
    const field2FieldOptions = computed(() => {
      if (isTimeseries.value) {
        const tsFields = getTimeseriesFields(selectedDataset.value)
        if (!tsFields) return []
        return tsFields.otherFields.map(f => ({
          value: f.key,
          label: f.label,
          rawKey: f.key,
        }))
      }
      // For non-timeseries views: all fields except processForm and currently selected
      return fieldOptions.value.filter(f => {
        if (f.processForm) return false
        if (f.value === selectedField.value) return false
        return true
      })
    })

    // When view changes to timeseries: apply defaults
    watch(selectedView, (view) => {
      if (view === 'timeseries') {
        const defaultField = getTimeseriesDefaultField(selectedDataset.value)
        if (defaultField) selectedField.value = defaultField
        selectedField2.value = ''
        selectedFreq.value = '1mo'
        // A dataset that opens its range keeps it: a 90-day trend would hide
        // the same catalog the open default exists to show.
        onFromChange(getDatasetModule(selectedDataset.value)?.defaultTimeRange?.from || 'now-90d')
        onToChange('')
      }
    })

    // ─── Wizard continue handler ──────────────────────────
    const onWizardContinue = ({ dataset, view, from, to, freq }) => {
      selectedDataset.value = dataset
      selectedView.value = view
      if (!getDatasetModule(dataset)?.omitTimeRange) {
        onFromChange(from || getDatasetDefaultFrom(dataset))
        onToChange(to || '')
      }
      if (freq) selectedFreq.value = freq
      wizardActive.value = false
    }

    // ─── Live toggle (reactive, initialized from prop) ─────
    const liveEnabled = ref(props.live)

    // ─── Stack group toggle ────────────────────────────────
    const stackGroupEnabled = ref(saved.stackGroup ?? false)

    const initStackGroup = () => {
      const module = getDatasetModule(selectedDataset.value)
      stackGroupEnabled.value = module?.viewDefaults?.[selectedView.value]?.stack === 'group'
    }

    watch([selectedDataset, selectedView], initStackGroup)
    // Skip the initial default when a persisted value was restored from savedConfig.
    if (saved.stackGroup === undefined || saved.stackGroup === null) initStackGroup()

    // ─── Top N by record count ─────────────────────────────
    // `limit` truncates without ranking, and it is not even an accepted
    // parameter of the chart views, so the N most frequent categories are
    // resolved with a separate aggregate read. See useTopRanked.
    // Offered only for the datasets in TOP_N_SUPPORTED_DATASETS. The intent is
    // seeded in the setup wizard and stays editable in the control panel.
    const {
      topRankedEnabled,
      topRankedCount,
      topRankedEffectiveCount,
      topRankedLoading,
      topRankedError,
      topRankedAvailable,
      topRankedOffered,
      topRankedActive,
      topRankedUnavailableReason,
      topRanked,
      setTopRankedEnabled,
      updateTopRankedCount,
      commitTopRankedCount,
      bindRankingSource,
    } = useTopRanked({ selectedDataset, selectedView, selectedField, fieldOptions, saved })

    // ─── Current status only ──────────────────────────────
    // Datasets whose status is joined from its history count a record once
    // per status change. See useCurrentStatus.
    const {
      currentStatusEnabled,
      currentStatusOffered,
      currentStatusLocked,
      currentStatusLabel,
      currentStatusApplied,
      currentStatusRelevant,
      currentStatusAffects,
      currentStatusLoading,
      currentStatusError,
      currentStatus,
      setCurrentStatusEnabled,
      openOnlyEnabled,
      openOnlyOffered,
      openOnlyClosedValues,
      openOnlyFilter,
      setOpenOnlyEnabled,
      applyCurrentStatusTo,
      bindCurrentStatusSource,
    } = useCurrentStatus({ selectedDataset, refreshInterval, saved })

    // ─── Extra params (optional advanced Streamlit params) ─
    const EXTRA_PARAM_KEYS = [
      'agg', 'by', 'columns', 'freq', 'innerRadius', 'legendPosition',
      'orientation', 'stack', 'trendline', 'targetRange', 'unit',
      'colorMapping', 'limit', 'plaintext', 'mapping', 'query',
      'where', 'aspectRatio', 'frameToken', 'renderBackground',
      'engine', 'palette', 'configuration',
    ]

    const extraParams = computed(() => {
      const out = {}
      EXTRA_PARAM_KEYS.forEach(key => {
        const val = props[key]
        if (val !== '' && val !== null && val !== undefined && val !== false) {
          out[key] = String(val)
        }
      })
      // Include selectedField (and optional field2) as 'by' parameter.
      // processForm fields use label as value; resolve to rawKey for the URL.
      if (selectedField.value && !out.by) {
        const fieldOpt = fieldOptions.value.find(f => f.value === selectedField.value)
        const byValue = fieldOpt?.rawKey || selectedField.value
        out.by = selectedField2.value
          ? `${byValue},${selectedField2.value}`
          : byValue
      }
      // Metric selector: send as `columns` (e.g. $sum_total_cost_service).
      // Default ($count) is omitted — backend treats absent columns as $count.
      if (selectedMetric.value && selectedMetric.value !== '$count' && !out.columns) {
        out.columns = selectedMetric.value
      }
      // Include selectedFreq as 'freq' parameter.
      if (selectedFreq.value && !out.freq) {
        out.freq = selectedFreq.value
      }
      // Stack group toggle (bar only): always set to override any viewDefault
      if (!out.stack && selectedView.value === 'bar') {
        out.stack = stackGroupEnabled.value ? 'group' : 'zero'
      }
      return out
    })

    // ─── URL builder ──────────────────────────────────────
    const heightRef = computed(() => props.height)
    const { iframeUrl, processFormConfig, chartUrlWithoutTopRanked, chartUrlUnrestricted } = useChartUrl({
      height: heightRef,
      baseUrl,
      selectedDataset,
      selectedView,
      theme,
      lang: selectedLang,
      token,
      activeFilters,
      live: liveEnabled,
      refreshInterval,
      extraParams,
      activeProcessFormLabel,
      processFormMode,
      processFormAnswer,
      processFormPairs,
      analysisAnswers,
      datasetRestriction,
      topRanked,
      currentStatus,
      openOnlyFilter,
    })

    // Wired after useChartUrl: the ranking reads the chart URL, and the chart
    // URL reads the ranking, so the source can only be bound once both exist.
    bindRankingSource(chartUrlWithoutTopRanked)
    bindCurrentStatusSource(chartUrlUnrestricted)

    // ─── Process-form URL builder ─────────────────────────
    // When processFormConfig is active, build an iframe URL that points
    // Streamlit to the process-chart API via data_format=json_2darray.
    const processFormUrl = ref('')

    // ─── Loading state ────────────────────────────────
    // Declared here (before the process-form watch) so the immediate run below
    // can flag loading while a restored process-form URL is rebuilt on mount.
    const isLoading = ref(false)

    watch(processFormConfig, (config) => {
      if (!config) {
        processFormUrl.value = ''
        return
      }

      // Build the full API URL for the process-chart endpoint
      const streamlitBase = baseUrl.value.replace(/\/+$/, '')
      const apiBase = streamlitBase.replace(/\/\/streamlit\./, '//api.')
      const apiParams = new URLSearchParams()
      apiParams.set('by', config.by)
      apiParams.set('view', config.view)
      apiParams.set('url', config.url)
      const apiUrl = `${apiBase}/v1/reports/process-chart?${apiParams.toString()}`

      // Build the iframe URL: Streamlit fetches data from the API URL
      const base = baseUrl.value.endsWith('/') ? baseUrl.value.slice(0, -1) : baseUrl.value
      const params = new URLSearchParams()
      params.set('embed', 'true')
      params.set('theme', theme.value)
      params.set('view', config.view)
      params.set('data_format', 'json_2darray')
      params.set('data', apiUrl)
      if (token.value) params.set('token', token.value)
      if (heightRef.value && heightRef.value > 0) {
        params.set('height', String(heightRef.value))
      }
      // Bar charts always get legend right + grouped stacking
      if (config.view === 'bar') {
        params.set('legendPosition', 'right')
        params.set('stack', 'group')
      }

      processFormUrl.value = `${base}/?${params.toString()}`
      isLoading.value = true
    }, { deep: true, immediate: true })

    // ─── Time range (from / to) ───────────────────────
    // Declared here (before defaultProcessFormUrl, which reads them) because
    // the immediate resolvedUrl watch below evaluates that computed during
    // setup when a process-form chart is restored.
    const selectedFrom = ref(props.from || activeFilters.value.from || getDatasetDefaultFrom(selectedDataset.value))
    const selectedTo = ref(props.to || activeFilters.value.to || '')

    // ─── Default process-form URL (overview before question/answer selection) ──
    const defaultProcessFormUrl = computed(() => {
      if (!isProcessFormField.value || !selectedDataset.value || !selectedView.value) return ''

      const module = getDatasetModule(selectedDataset.value)
      if (!module?.fields) return ''

      const matchedField = module.fields.find(f => (f.processForm ? f.label : f.key) === selectedField.value)
      const dataColumn = matchedField?.key || module.analysisConfig?.dataColumn || `${selectedDataset.value}.data`
      const dataKey = dataColumn.split('.').pop()

      const streamlitBase = baseUrl.value.replace(/\/+$/, '')
      const apiBase = streamlitBase.replace(/\/\/streamlit\./, '//api.')

      // Inner CSV URL
      const csvParams = new URLSearchParams()
      csvParams.set('dataset', selectedDataset.value)
      csvParams.set('limit', '-1')
      csvParams.set('columns', dataKey)
      csvParams.set('view', 'csv')
      if (!omitsTimeRange.value) {
        const rawFrom = selectedFrom.value || getDatasetDefaultFrom(selectedDataset.value)
        csvParams.set('from', resolveTimeBound(rawFrom))
        if (selectedTo.value) csvParams.set('to', resolveTimeBound(selectedTo.value, { end: true }))
      }
      if (token.value) csvParams.set('token', token.value)

      // Propagate where clause filters from iframeUrl so normal filters
      // (status, installation, time, etc.) apply to the overview chart
      if (iframeUrl.value) {
        const iframeQuery = iframeUrl.value.split('/?')[1]
        if (iframeQuery) {
          const whereParam = new URLSearchParams(iframeQuery).get('where')
          if (whereParam) csvParams.set('where', whereParam)
        }
      }

      const csvUrl = `${streamlitBase}/?${csvParams.toString()}`

      // API URL (process-chart, no 'by')
      const apiParams = new URLSearchParams()
      apiParams.set('view', selectedView.value)
      apiParams.set('url', csvUrl)
      const apiUrl = `${apiBase}/v1/reports/process-chart?${apiParams.toString()}`

      // Iframe URL
      const params = new URLSearchParams()
      params.set('embed', 'true')
      params.set('theme', theme.value)
      if (token.value) params.set('token', token.value)
      if (heightRef.value && heightRef.value > 0) params.set('height', String(heightRef.value))
      params.set('view', selectedView.value)
      params.set('data_format', 'json_2darray')
      // Bar charts always get legend right + grouped stacking; others hide legend
      if (selectedView.value === 'bar') {
        params.set('legendPosition', 'right')
        params.set('stack', 'group')
      } else {
        params.set('legendPosition', 'off')
      }
      params.set('data', apiUrl)

      return `${streamlitBase}/?${params.toString()}`
    })

    // ─── Editable URL ─────────────────────────────────
    const editableUrl = ref('')
    const manualUrl = ref('')
    const copied = ref(false)

    const applyManualUrl = () => {
      const val = editableUrl.value.trim()
      if (val && val !== resolvedUrl.value) {
        manualUrl.value = val
        isLoading.value = true
      }
    }

    const copyUrl = () => {
      const url = editableUrl.value || iframeUrl.value
      if (navigator.clipboard) {
        navigator.clipboard.writeText(url)
      }
      copied.value = true
      setTimeout(() => { copied.value = false }, 1500)
    }

    // ─── Downloads (CSV / PDF) ────────────────────
    const triggerDownload = async (endpoint, filename, { forceViewCsv = false, forceThemeLight = false, sourceUrl = null } = {}) => {
      const currentUrl = sourceUrl || iframeUrl.value
      if (!currentUrl) return

      let url = forceViewCsv ? currentUrl.replace(/([?&])view=[^&]*/, '$1view=csv') : currentUrl
      if (forceThemeLight) url = url.replace(/([?&])theme=[^&]*/, '$1theme=light')
      if (!omitsTimeRange.value && !/[?&]from=/.test(url)) url += `&from=${encodeURIComponent(getDatasetDefaultFrom(selectedDataset.value))}`

      // Épica 5: Freeze relative time ranges for downloads to avoid unnecessary queries
      const fromMatch = url.match(/[?&]from=([^&]*)/)
      const toMatch = url.match(/[?&]to=([^&]*)/)
      if (fromMatch && fromMatch[1].includes('now')) {
        const frozen = freezeTimeRange(decodeURIComponent(fromMatch[1]), toMatch ? decodeURIComponent(toMatch[1]) : '')
        url = url.replace(/([?&])from=[^&]*/, `$1from=${encodeURIComponent(frozen.from)}`)
        if (toMatch) {
          url = url.replace(/([?&])to=[^&]*/, `$1to=${encodeURIComponent(frozen.to)}`)
        } else {
          url += `&to=${encodeURIComponent(frozen.to)}`
        }
      }

      if (forceViewCsv) {
        const dsModule = getDatasetModule(selectedDataset.value)

        // Inject viewDefaults for the csv view (e.g. limit=-1) since the URL
        // was originally built for a chart view that may not include them.
        const csvViewDefaults = dsModule?.viewDefaults?.csv
        if (csvViewDefaults) {
          Object.entries(csvViewDefaults).forEach(([k, v]) => {
            if (v !== '' && v !== null && v !== undefined) {
              const re = new RegExp(`([?&])${k}=[^&]*`)
              if (re.test(url)) {
                url = url.replace(re, `$1${k}=${v}`)
              } else {
                url += `&${k}=${v}`
              }
            }
          })
        }

        // Épica 9: Use csvConfig if available, fallback to legacy csvColumns
        const csvCfg = dsModule?.csvConfig
        const hasColumns = /[?&]columns=/.test(url)

        if (csvCfg) {
          // Apply extended CSV config
          if (csvCfg.columns && !hasColumns) {
            url = url.replace(/([?&])by=[^&]*/, `$1columns=${csvCfg.columns}`)
          } else {
            url = url.replace(/([?&])by=[^&]*/, '')
          }
          if (csvCfg.withTables) url += `&with_tables=${csvCfg.withTables}`
          if (csvCfg.exceptColumns) url += `&except_columns=${csvCfg.exceptColumns}`
          if (csvCfg.qualifiedColumns) url += '&qualified_columns=true'
        } else if (!hasColumns && dsModule?.csvColumns) {
          // Legacy: use csvColumns string
          url = url.replace(/([?&])by=[^&]*/, '$1columns=' + dsModule.csvColumns)
        } else {
          url = url.replace(/([?&])by=[^&]*/, '')
        }
      }

      const loadingId = toast(
        { component: ToastificationContent, props: { title: 'Generando descarga...', icon: 'LoaderIcon', variant: 'info' } },
        { timeout: false },
      )

      try {
        // The XLSX reads the status even when the chart does not.
        url = await applyCurrentStatusTo(url)
        const response = await axios.post(endpoint, { url }, { responseType: 'blob' })

        // Backend may return 200 with a JSON error body instead of a real file (XLSX endpoint)
        if (endpoint === '/v1/reports/download' && response.data && response.data.type && response.data.type.includes('json')) {
          const text = await response.data.text()
          let parsed = null
          try { parsed = JSON.parse(text) } catch (_) { /* ignore */ }
          console.log('[ChartsEmbedder] XLSX JSON response:', parsed)
          if (parsed && parsed.success === false) {
            toast.dismiss(loadingId)
            toast({ component: ToastificationContent, props: { title: parsed.data || parsed.message || 'Error al generar la descarga', icon: 'AlertTriangleIcon', variant: 'danger' } })
            return
          }
        }

        toast.dismiss(loadingId)
        toast({ component: ToastificationContent, props: { title: 'Descarga lista', icon: 'CheckCircleIcon', variant: 'success' } })

        const blobUrl = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = blobUrl
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(blobUrl)
      } catch (err) {
        toast.dismiss(loadingId)
        let errorTitle = 'Error al generar la descarga'

        if (endpoint === '/v1/reports/download' && err.response && err.response.data) {
          try {
            const raw = err.response.data
            let text = raw
            if (raw instanceof Blob) text = await raw.text()
            else if (raw && typeof raw.text === 'function') text = await raw.text()
            else if (raw instanceof ArrayBuffer) text = new TextDecoder().decode(raw)
            const parsed = typeof text === 'string' ? JSON.parse(text) : text
            console.log('[ChartsEmbedder] XLSX error payload:', parsed)
            if (parsed && parsed.success === false && parsed.data) errorTitle = 'Sin información para descargar';
          } catch (parseErr) {
            console.warn('[ChartsEmbedder] Could not parse XLSX error payload:', parseErr)
          }
        }

        toast({ component: ToastificationContent, props: { title: errorTitle, icon: 'AlertTriangleIcon', variant: 'warning' } })
        console.error('[ChartsEmbedder] Download failed:', err)
      }
    }

    // Some datasets (operational_security, billing_finances) export through a
    // dedicated backend endpoint instead of the generic Streamlit CSV proxy.
    // The dataset module declares `apiExport` with the endpoint and the
    // active-filter keys to forward.
    const triggerApiExport = async (cfg) => {
      const params = { ...(cfg.baseParams || {}) }
      const filters = activeFilters.value || {}
      Object.entries(cfg.filters || {}).forEach(([apiKey, srcKey]) => {
        let val
        if (srcKey === 'from') val = selectedFrom.value
        else if (srcKey === 'to') val = selectedTo.value
        else val = filters[srcKey]
        if (val === null || val === undefined || val === '') return
        if (apiKey === 'from' || apiKey === 'to') {
          val = resolveTimeBound(String(val), { end: apiKey === 'to' })
          if (val === 'now') return
        }
        params[apiKey] = val
      })

      const accessToken = localStorage.getItem('accessToken') || ''
      const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : {}

      const loadingId = toast(
        { component: ToastificationContent, props: { title: 'Generando descarga...', icon: 'LoaderIcon', variant: 'info' } },
        { timeout: false },
      )

      try {
        const response = await axios.get(cfg.endpoint, { params, headers, responseType: 'blob' })

        // Backend may return 200 with a JSON error body instead of a real file
        if (response.data && response.data.type && response.data.type.includes('json')) {
          const text = await response.data.text()
          let parsed = null
          try { parsed = JSON.parse(text) } catch (_) { /* ignore */ }
          if (parsed && parsed.success === false) {
            toast.dismiss(loadingId)
            toast({ component: ToastificationContent, props: { title: parsed.data || parsed.message || 'Sin información para descargar', icon: 'AlertTriangleIcon', variant: 'warning' } })
            return
          }
        }

        toast.dismiss(loadingId)
        toast({ component: ToastificationContent, props: { title: 'Descarga lista', icon: 'CheckCircleIcon', variant: 'success' } })

        const blobUrl = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = blobUrl
        link.setAttribute('download', cfg.filename || 'Reporte.xlsx')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(blobUrl)
      } catch (err) {
        toast.dismiss(loadingId)
        toast({ component: ToastificationContent, props: { title: 'Error al generar la descarga', icon: 'AlertTriangleIcon', variant: 'warning' } })
        console.error('[ChartsEmbedder] API export failed:', err)
      }
    }

    const onDownloadCsv = () => {
      const dsModule = getDatasetModule(selectedDataset.value)
      if (dsModule?.apiExport) return triggerApiExport(dsModule.apiExport)
      return triggerDownload('/v1/reports/download', 'Reporte.xlsx', { forceViewCsv: true })
    }
    const onDownloadPdf = () => triggerDownload('/v1/charts-api/get-pdf-url', 'Reporte.pdf', { forceThemeLight: true, sourceUrl: resolvedUrl.value })

    // ─── Question-count mode: fetch data + build URL ──────────────────
    // Declared here (before resolvedUrl, which reads it) because the immediate
    // watch below evaluates resolvedUrl during setup when a chart is restored
    // in question-count mode.
    const { chartData } = useProcessFormChart({
      selectedQuestions,
      filterOptionsMap,
    })

    const questionCountUrl = computed(() => {
      if (!chartData.value || !selectedView.value) return ''
      const base = baseUrl.value.endsWith('/') ? baseUrl.value.slice(0, -1) : baseUrl.value
      const params = new URLSearchParams()
      params.set('embed', 'true')
      params.set('theme', theme.value)
      params.set('view', selectedView.value)
      const payload = {
        headers: ['Total', 'Pregunta', 'Respuesta'],
        data: chartData.value,
      }
      params.set('data', JSON.stringify(payload))
      params.set('data_format', 'json_raw2darray')
      if (token.value) params.set('token', token.value)
      if (heightRef.value && heightRef.value > 0) params.set('height', String(heightRef.value))
      // Bar charts always get legend right + grouped stacking
      if (selectedView.value === 'bar') {
        params.set('legendPosition', 'right')
        params.set('stack', 'group')
      }
      return `${base}/?${params.toString()}`
    })

    // The actual URL sent to the iframe: manual override → process-form → computed
    const resolvedUrl = computed(() => {
      if (manualUrl.value) return manualUrl.value
      // In analysis mode, use iframeUrl directly (where filters are embedded in URL)
      if (isProcessFormField.value && processFormMode.value === 'analysis') return iframeUrl.value
      // In question-count mode: show count chart for selected questions
      if (isProcessFormField.value && isQuestionCountMode.value) return questionCountUrl.value || defaultProcessFormUrl.value
      // In count mode: filtered URL → default overview → empty
      if (isProcessFormField.value) return processFormUrl.value || defaultProcessFormUrl.value
      return iframeUrl.value
    })

    // Sync editable URL when resolved (iframe-rendered) URL changes
    watch(resolvedUrl, (url) => {
      editableUrl.value = url
    }, { immediate: true })

    // Clear manual override only when the underlying computed URL changes naturally
    watch([iframeUrl, processFormUrl], () => {
      manualUrl.value = ''
    })

    watch(resolvedUrl, (url, oldUrl) => {
      if (url && url !== oldUrl) isLoading.value = true
    })

    // ─── Filter change handler ────────────────────────────
    const onFilterChange = ({ key, value }) => setFilter(key, value)
    const onAnalysisAnswerChange = ({ question, answer }) => setAnalysisAnswer(question, answer)

    // ─── From / To (time range) as primary controls ───────
    const onFromChange = (val) => {
      const next = val || getDatasetDefaultFrom(selectedDataset.value)
      selectedFrom.value = next
      setFilter('from', next)
    }

    const onToChange = (val) => {
      selectedTo.value = val || ''
      setFilter('to', val || '')
    }

    // Keep selectedFrom/To in sync if filters change externally
    watch(() => activeFilters.value.from, (val) => {
      selectedFrom.value = val || getDatasetDefaultFrom(selectedDataset.value)
    })
    watch(() => activeFilters.value.to, (val) => {
      selectedTo.value = val || ''
    })

    // Follow the new dataset default range, but only while the range still is
    // the previous dataset default: switching to one that opens its range must
    // not keep a 30-day window that hides its catalog, and switching away must
    // not drag the open range into a dataset that gets one row per event.
    watch(selectedDataset, (dataset, previous) => {
      if (!previous || dataset === previous) return
      if (getDatasetModule(dataset)?.omitTimeRange) return
      if (selectedFrom.value !== getDatasetDefaultFrom(previous)) return
      onFromChange(getDatasetDefaultFrom(dataset))
    })

    // ─── Sync external prop changes (SDK use-case) ────────
    watch(() => props.dataset, (val) => { if (val) selectedDataset.value = val })
    watch(() => props.view, (val) => { if (val) selectedView.value = val })
    watch(() => props.live, (val) => { liveEnabled.value = val })

    // ─── Persisted configuration ──────────────────────────
    // Everything needed to rebuild this exact chart after the host view is
    // saved or the page is reloaded. Emitted upward so the slot can store it
    // back into its `data`. from/to live inside activeFilters, so they ride
    // along in `filters`.
    const chartConfig = computed(() => ({
      dataset: selectedDataset.value,
      view: selectedView.value,
      field: selectedField.value,
      field2: selectedField2.value,
      metric: selectedMetric.value,
      freq: selectedFreq.value,
      live: liveEnabled.value,
      stackGroup: stackGroupEnabled.value,
      // Only the intent is persisted; the winning values are derived from live
      // data and would go stale as soon as the records change.
      topNEnabled: topRankedEnabled.value,
      topNCount: topRankedEffectiveCount.value,
      currentStatusOnly: currentStatusEnabled.value,
      openOnly: openOnlyEnabled.value,
      filters: { ...activeFilters.value },
      processFormLabel: activeProcessFormLabel.value,
      processFormPairs: processFormPairs.value.map(p => ({ ...p })),
      selectedQuestions: [...selectedQuestions.value],
      analysisAnswers: { ...analysisAnswers },
    }))

    // Debounced emit — only once a real selection exists (skip the wizard
    // phase). Restored values don't fire this: the watcher isn't immediate and
    // every ref was seeded before it was registered.
    watch(chartConfig, (cfg) => {
      if (!hasSelection.value) return
      clearTimeout(configEmitTimer)
      configEmitTimer = setTimeout(() => {
        emit('update:config', JSON.parse(JSON.stringify(cfg)))
      }, 400)
    })

    return {
      rootEl,
      user,
      iframeRenderKey,
      containerStyle,
      panelOpen,
      isDark,
      wizardActive,
      selectedDataset,
      selectedView,
      selectedField,
      selectedField2,
      datasetOptions,
      viewOptions,
      fieldOptions,
      effectiveFieldOptions,
      isTimeseries,
      field2FieldOptions,
      hasSelection,
      filterKeys,
      activeFilters,
      lockedFilters,
      filterOptionsMap,
      getOptionsForFilter,
      iframeUrl,
      resolvedUrl,
      editableUrl,
      copied,
      applyManualUrl,
      copyUrl,
      isLoading,
      t,
      translateFilter: (key) => translateFilter(key, activeProcessFormLabel.value),
      onFilterChange,
      onWizardContinue,
      selectedFrom,
      selectedTo,
      onFromChange,
      onToChange,
      liveEnabled,
      stackGroupEnabled,
      topRankedEnabled,
      topRankedCount,
      topRankedLoading,
      topRankedError,
      topRankedAvailable,
      topRankedOffered,
      topRankedActive,
      topRankedUnavailableReason,
      setTopRankedEnabled,
      updateTopRankedCount,
      commitTopRankedCount,
      currentStatusEnabled,
      currentStatusOffered,
      currentStatusLocked,
      currentStatusLabel,
      currentStatusApplied,
      currentStatusRelevant,
      currentStatusAffects,
      currentStatusLoading,
      currentStatusError,
      setCurrentStatusEnabled,
      openOnlyEnabled,
      openOnlyOffered,
      openOnlyClosedValues,
      setOpenOnlyEnabled,
      TOP_N_MIN: MIN_TOP_N,
      TOP_N_MAX: MAX_TOP_N,
      isProcessFormField,
      hasProcessForm,
      activeProcessFormLabel,
      isMultiQuestionnaire,
      questionnaireOptions,
      setActiveProcessFormLabel,
      processFormPairs,
      setProcessFormAnswerForQuestion,
      clearProcessFormPairs,
      selectedQuestions,
      toggleSelectedQuestion,
      clearSelectedQuestions,
      selectedFreq,
      dateGroupingPresets,
      omitsTimeRange,
      selectedMetric,
      metricOptions,
      onDownloadCsv,
      onDownloadPdf,
    }
  },
}
</script>

<style scoped>
.charts-embedder {
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 6px;
  background: #f5f5f5;
  color: #333;
  position: relative;
}

.charts-embedder--dark {
  background: var(--bg, #1a1c24);
  color: #e0e0e0;
}


/* ─── Iframe (always fills the full container) ──── */
.charts-embedder__iframe-wrapper {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

/* ─── Gear Toggle Button ────────────────────────── */
.charts-embedder__gear {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.85);
  color: #555;
  cursor: pointer;
  transition: transform 0.3s ease, background 0.2s, opacity 0.2s;
  opacity: 0.6;
}

.charts-embedder__gear:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.95);
}

.charts-embedder__gear--open {
  opacity: 1;
  transform: rotate(90deg);
}

.charts-embedder--dark .charts-embedder__gear {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.12);
  color: #ccc;
}

.charts-embedder--dark .charts-embedder__gear:hover {
  background: rgba(255, 255, 255, 0.15);
}

/* ─── Backdrop (click-outside to close) ─────────── */
.charts-embedder__backdrop {
  position: absolute;
  inset: 0;
  z-index: 11;
  display: flex;
  flex-direction: column;
}

/* ─── Control Panel (glassmorphism overlay) ─────── */
.charts-embedder__controls {
  padding: 14px 16px;
  background: rgba(20, 22, 30, 0.82);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
  max-height: 80vh;
  overflow-y: auto;
}

.charts-embedder--dark .charts-embedder__controls {
  background: rgba(14, 17, 23, 0.85);
}

/* Light mode */
:not(.charts-embedder--dark)>.charts-embedder__backdrop>.charts-embedder__controls {
  background: rgba(245, 245, 245, 0.88);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom-color: rgba(0, 0, 0, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}

/* Light mode — Analysis panel overrides */
:not(.charts-embedder--dark)>>>.chart-filter-panel__analysis-body {
  background: rgba(0, 0, 0, 0.04);
  border-color: rgba(0, 0, 0, 0.08);
}



:not(.charts-embedder--dark)>>>.chart-filter-panel__analysis-clear {
  border-color: rgba(200, 50, 50, 0.2);
  background: rgba(200, 50, 50, 0.06);
  color: #cc4444;
}

/* ─── Overlay Transition (slide down + fade) ────── */
.charts-embedder-overlay-enter-active {
  transition: opacity 0.3s ease;
}

.charts-embedder-overlay-leave-active {
  transition: opacity 0.25s ease;
}

.charts-embedder-overlay-enter,
.charts-embedder-overlay-leave-to {
  opacity: 0;
}

.charts-embedder-overlay-enter-active .charts-embedder__controls {
  animation: ce-slide-down 0.3s ease forwards;
}

.charts-embedder-overlay-leave-active .charts-embedder__controls {
  animation: ce-slide-up 0.25s ease forwards;
}

@keyframes ce-slide-down {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes ce-slide-up {
  from {
    transform: translateY(0);
    opacity: 1;
  }

  to {
    transform: translateY(-20px);
    opacity: 0;
  }
}

/* ─── vue-select theme (dark) ───────────────────── */
.charts-embedder--dark>>>.vs__dropdown-toggle {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 4px;
  min-height: 34px;
}

.charts-embedder--dark>>>.vs__selected {
  color: #e0e0e0;
}

.charts-embedder--dark>>>.vs__search::placeholder {
  color: rgba(255, 255, 255, 0.35);
}

.charts-embedder--dark>>>.vs__open-indicator,
.charts-embedder--dark>>>.vs__clear {
  fill: rgba(255, 255, 255, 0.4);
}

.charts-embedder--dark>>>.vs__dropdown-menu {
  background: #1e2029;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.charts-embedder--dark>>>.vs__dropdown-option {
  color: #d0d0d0;
}

.charts-embedder--dark>>>.vs__dropdown-option--highlight {
  background: rgba(0, 104, 201, 0.35);
  color: #fff;
}

.charts-embedder--dark>>>.chart-control-panel__label,
.charts-embedder--dark>>>.chart-filter-panel__label,
.charts-embedder--dark>>>.chart-time-filter__label {
  color: rgba(255, 255, 255, 0.55);
}

/* ─── ChartTimeFilter dark mode ────────────────── */
.charts-embedder--dark>>>.chart-time-filter__datepicker,
.charts-embedder--dark>>>.chart-time-filter__input {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.12);
  color: #e0e0e0;
}

/* ─── URL Bar ───────────────────────────────────── */
.charts-embedder__url-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.charts-embedder__url-input {
  flex: 1;
  font-family: monospace;
  font-size: 0.78rem;
  padding: 5px 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.06);
  color: inherit;
  outline: none;
  min-width: 0;
}

.charts-embedder__url-input:focus {
  border-color: #0068c9;
}

.charts-embedder__url-btn {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.06);
  color: inherit;
  cursor: pointer;
  font-size: 0.85rem;
  transition: background 0.15s;
}

.charts-embedder__url-btn:hover {
  background: rgba(0, 104, 201, 0.15);
}

.charts-embedder__url-btn--apply {
  background: rgba(0, 104, 201, 0.2);
  border-color: rgba(0, 104, 201, 0.4);
}
</style>
